import React from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import "./NewsCardBig.css";

/* ------------ focus helpers: read focal point from block ------------- */
const clamp01 = (n) => Math.max(0, Math.min(1, Number(n)));
const parseMaybeJson = (v) => {
  if (!v) return null;
  if (typeof v === "string") {
    try { return JSON.parse(v); } catch { return null; }
  }
  return v;
};

// prefer crop_json; else use last entry of image_json (array or string)
function getFocusJsonFromBlock(block) {
  if (!block) return null;
  if (block.crop_json) return parseMaybeJson(block.crop_json);

  const ij = block.image_json;
  if (!ij) return null;
  if (Array.isArray(ij)) return ij.length ? parseMaybeJson(ij[ij.length - 1]) : null;
  return parseMaybeJson(ij);
}

function objectPositionFromBlock(block) {
  const j = getFocusJsonFromBlock(block);
  const fp = j?.focalPoint;
  if (!fp || typeof fp.x !== "number" || typeof fp.y !== "number") {
    return "50% 50%";
  }
  const xPct = (clamp01(fp.x) * 100).toFixed(2);
  const yPct = (clamp01(fp.y) * 100).toFixed(2);
  return `${xPct}% ${yPct}%`;
}

function pickImageBlock(blocks) {
  if (!Array.isArray(blocks)) return null;
  // prefer an image that actually has focus/crop data
  return (
    blocks.find((b) => b.type === "image" && (b.crop_json || b.image_json)) ||
    blocks.find((b) => b.type === "image") ||
    null
  );
}

/* ---------- Markdown/link helpers ------------------------------- */
const ensureHttp = (href = "") => {
  if (!href) return href;
  if (
    href.startsWith("/") ||
    href.startsWith("#") ||
    href.startsWith("mailto:") ||
    href.startsWith("tel:") ||
    /^https?:\/\//i.test(href)
  ) return href;
  if (href.startsWith("www.")) return `https://${href}`;
  return href;
};

// get first paragraph of markdown (keeps links intact)
const firstParagraph = (md = "") => (md || "").split(/\n\s*\n/)[0];

const toISO = (d) => {
  if (!d) return undefined;
  const dt = d instanceof Date ? d : new Date(d);
  return isNaN(dt.getTime()) ? undefined : dt.toISOString();
};

/* ---------------- component ---------------- */
const NewsCardBig = ({ item, title, date, excerpt, image, news_id, category }) => {
  // Prefer item if passed; otherwise take direct props
  const firstTextBlock = item?.blocks?.find((b) => b.type === "text");
  const mdFromItem = firstTextBlock?.body_md || firstTextBlock?.body || "";

  const imgBlock = pickImageBlock(item?.blocks);
  const imgPath = imgBlock?.path || item?.blocks?.find((b) => b.type === "image")?.path;

  const dateDisplayFromItem = item?.published_at
    ? new Date(item.published_at).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
    : null;

  const dateTimeISOFromItem = item?.published_at ? toISO(item.published_at) : undefined;

  const cardData = item
    ? {
      title: item.title,
      dateDisplay: dateDisplayFromItem,
      dateTimeISO: dateTimeISOFromItem,
      excerpt: mdFromItem,
      image: imgPath,
      category: item.category,
      objectPosition: objectPositionFromBlock(imgBlock),
      news_id: item.news_id || item.id,
    }
    : {
      title,
      dateDisplay: date || null,
      dateTimeISO: toISO(date),
      excerpt,
      image,
      category,
      objectPosition: "50% 50%",
      news_id,
    };

  // Markdown components (links open in new tab if external)
  const mdComponents = {
    p: ({ node, ...props }) => <p className="big-news-excerpt" {...props} />,
    a: ({ node, href, children, ...props }) => {
      const safeHref = ensureHttp(href || "");
      let isExternal = false;
      try {
        const base = typeof window !== "undefined" ? window.location.href : "http://localhost";
        const url = new URL(safeHref, base);
        isExternal =
          /^https?:$/i.test(url.protocol) &&
          (typeof window === "undefined" || url.origin !== window.location.origin);
      } catch { }
      return (
        <a
          href={safeHref}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          {...props}
        >
          {children}
        </a>
      );
    },
    // Disallow inline images inside excerpt to avoid layout shifts
    img: () => null,
  };

  const excerptMd = firstParagraph(cardData.excerpt || "");

  return (
    <article className="big-news-card">
      <div className="big-news-title-container">
        <div className="big-news-title-container-inner">
          <div className="big-news-title">{cardData.title?.trim()}</div>
        </div>
        <div className="big-news-header">
          {cardData.category && (
            <div className="big-news-category-container">
              <p className="big-news-category">{cardData.category}</p>
            </div>
          )}
          {cardData.dateDisplay && (
            <time className="big-news-date" dateTime={cardData.dateTimeISO || undefined}>
              {cardData.dateDisplay}
            </time>
          )}
        </div>
      </div>

      {excerptMd && (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSanitize]}
          components={mdComponents}
        >
          {excerptMd}
        </ReactMarkdown>
      )}

      {cardData.image && (
        <div className="big-news-img-wrapper">
          <img
            src={cardData.image}
            alt={cardData.title || "news"}
            className="news-img"
            loading="lazy"
            style={{ objectFit: "cover", objectPosition: cardData.objectPosition }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
      )}

      <footer className="news-footer">
        <div className="big-news-btn-container">
          <Link
            to={`/news/${cardData.news_id}`}
            className="news-read-more news-card-btn"
            aria-label={`Read more about ${cardData.title}`}
          >
            Read more
          </Link>
        </div>
      </footer>
    </article>
  );
};

export default NewsCardBig;
